<template>
    <div class="AdminImports">
        <WCard class="ImportList">
            <div class="Filters flex row-stretch-center">
                <Tabs
                    v-model="activeTab"
                    :tabs="tabs"
                    @input="handleResetFilters"
                />

                <ImportFilters
                    v-model="currentFilters"
                    :filters="filters"
                    @onSubmit="onSubmitImportFilters"
                    @onReset="handleResetFilters"
                />
            </div>

            <div class="Container flex col-center">
                <FadeTransition
                    v-if="isLoading"
                    mode="out-in"
                >
                    <WLoading visible />
                </FadeTransition>
                <div
                    v-else
                    class="Wrapper flex-1"
                >
                    <ImportList
                        :items="importsFiltered"
                        @onView="handleView"
                        @onArchive="handleArchive"
                        @onCancel="handleCancel"
                        @onUnarchive="handleUnarchive"
                        @onNewImport="handleNewImport"
                        @onDownload="handleDownload"
                    />

                    <div
                        v-if="enablePagination"
                        class="Pagination flex row-center"
                    >
                        <WPaginate
                            v-if="(importsFiltered || []).length"
                            :selected="(pagination || {}).page || 1"
                            :pages="(pagination || {}).lastPage || 1"
                            class="mt-1 mb-1"
                            @onChange="handlePagination"
                        />
                    </div>
                </div>
            </div>
        </WCard>

        <Modal
            :opened="modal === modals.CREATE"
            :title="$t('imports.addImport')"
            #body
            @close="handleCleanModal"
        >
            <ImportForm
                @onCreate="handleCreateImport"
                @onReset="handleCleanModal"
            />
        </Modal>

        <Modal
            :opened="modal === modals.EVOLUTION"
            :title="$t('imports.evolutionModal.title')"
            #body
            @close="handleCleanModal"
        >
            <EvolutionModal v-if="modal === modals.EVOLUTION" />
        </Modal>

        <ModalRequest
            :opened="modal === modals.CANCEL"
            :title="$t('imports.modals.cancel.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            @close="handleCleanModal"
            @onAccept="cancelImport"
        />

        <ModalRequest
            :opened="modal === modals.ARCHIVE"
            :title="$t('imports.modals.archive.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            @close="handleCleanModal"
            @onAccept="archiveImport"
        />

        <ModalRequest
            :opened="modal === modals.UNARCHIVE"
            :title="$t('imports.modals.unarchive.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            @close="handleCleanModal"
            @onAccept="unarchiveImport"
        />
    </div>
</template>

<script>
import {
    Api, apiRequest, rethrowError, notifySuccess, notifyError,
} from '@/api'
import { ModalRequest, Tabs, Modal } from '@/components'
import { entityMixin } from '@/mixins'
import { STATUS, SOURCES } from '@/entities/imports'
import fileDownload from 'js-file-download'
import { ImportList, ImportFilters, ImportForm, EvolutionModal } from './components'

const MODAL_TYPES = {
    CREATE: 'CREATE',
    CANCEL: 'CANCEL',
    ARCHIVE: 'ARCHIVE',
    UNARCHIVE: 'UNARCHIVE',
    EVOLUTION: 'EVOLUTION',
}

const FILTERS = [
    {
        name: 'name',
        type: 'text',
        placeholder: 'Find import',
        value: '',
    },
    {
        name: 'date',
        type: 'date',
        placeholder: 'Date',
    },
    {
        name: 'status',
        type: 'select',
        placeholder: 'Status',
        value: '',
    },
]

const STATUS_FILTERS = [
    STATUS.running,
    STATUS.pendingReview,
    STATUS.completed,
    STATUS.cancelled,
    STATUS.failed,
]

const CURRENT_FILTERS = {
    name: '',
    status: '',
    date: undefined,
}

const TABS = {
    HISTORY: 'history',
    OCR_UPLOADS: 'ocr-uploads',
    ARCHIVE: 'archive',
}

const PER_PAGE = 20
const PAGINATION = {
    page: 1,
    perPage: PER_PAGE,
}

export default {
    name: 'AdminImports',
    components: {
        ModalRequest,
        Tabs,
        Modal,
        ImportList,
        ImportForm,
        ImportFilters,
        EvolutionModal,
    },
    mixins: [entityMixin],
    entity () {
        return {
            reload: true,
            buttons: [{
                iconLeft: 'bar-chart-2',
                text: this.$t('imports.evolution'),
                onClick: this.handleEvolution,
                outline: true,
            }, {
                iconLeft: 'plus-circle',
                text: this.$t('imports.addImport'),
                onClick: this.handleNewImport,
            }],
        }
    },
    metaInfo () {
        return {
            title: this.$t('admin.navigation.imports.list'),
        }
    },
    data () {
        const status = STATUS_FILTERS.map(key => ({ key, label: this.$t(`imports.status.${key}`) }))
        const filters = FILTERS.map(filter => (filter.name === 'status' ? { ...filter, options: status } : filter))
        const tabs = [
            {
                key: TABS.HISTORY,
                text: this.$t('imports.list.tabs.history'),
            },
            {
                key: TABS.OCR_UPLOADS,
                text: this.$t('imports.list.tabs.ocrUploads'),
            },
            {
                key: TABS.ARCHIVE,
                text: this.$t('imports.list.tabs.archives'),
            },
        ]

        return {
            filters,
            currentFilters: CURRENT_FILTERS,

            tabs,
            activeTab: tabs[0].key,

            imports: undefined,
            pagination: PAGINATION,
            isLoading: false,

            selected: undefined,
            modal: undefined,
            modals: MODAL_TYPES,
            isFetching: false,
        }
    },
    computed: {
        breadcrumbs () {
            return [
                {
                    key: 'AdminImports',
                    label: this.$t('imports.title'),
                    to: { name: 'AdminImports' },
                },
            ]
        },
        importsFiltered () {
            return (this.imports || []).map(this.normalizeImports)
        },
        enablePagination () {
            return (this.pagination || {}).lastPage > 1
        },
    },
    mounted () {
        this.actions = {
            VIEW: {
                icon: 'eye',
                linkColor: 'secondary',
                disabled: false,
                event: 'onView',
                label: this.$t('imports.actions.view'),
            },
            DOWNLOAD: {
                icon: 'download',
                linkColor: 'secondary',
                disabled: false,
                event: 'onDownload',
                label: this.$t('imports.actions.download'),
            },
            ARCHIVE: {
                icon: 'save',
                linkColor: 'secondary',
                event: 'onArchive',
                label: this.$t('imports.actions.archive'),
            },
            UNARCHIVE: {
                icon: 'save',
                linkColor: 'secondary',
                event: 'onUnarchive',
                label: this.$t('imports.actions.unarchive'),
            },
            CANCEL: {
                icon: 'trash',
                linkColor: 'secondary',
                event: 'onCancel',
                label: this.$t('imports.actions.cancel'),
            },
        }

        this.fetchImports()
    },
    methods: {
        normalizeImports (item) {
            const {
                VIEW, CANCEL, ARCHIVE, DOWNLOAD,
            } = this.actions
            const view = ![STATUS.pending, STATUS.queued, STATUS.running].includes(item.status)
            const cancel = !![STATUS.queued, STATUS.running].includes(item.status)
            const showDownload = !!(item.fileUploadId)
            const actions = [
                ...(view ? [VIEW] : []),
                ...(cancel ? [CANCEL] : []),
                ARCHIVE,
                ...(showDownload ? [DOWNLOAD] : []),
            ]

            return { ...item, actions }
        },
        handlePagination (page) {
            this.pagination = { ...this.pagination, page }
            this.fetchImports()
        },
        /** Evento que se ejecuta cuando se aplican los filtros. */
        onSubmitImportFilters () {
            // Cuando se aplica el filtro, debe volver a la primera página.
            this.pagination.page = 1
            this.fetchImports()
        },
        fetchImports () {
            const { name, status, date } = this.currentFilters
            const { page, perPage } = this.pagination
            const params = {
                ...(this.activeTab === TABS.ARCHIVE ? { archived: true } : {}),
                ...(this.activeTab === TABS.OCR_UPLOADS ? { source: SOURCES.OCR_STACKAI } : {}),
                ...(name ? { text: name } : {}),
                ...(status ? { status } : {}),
                ...(date ? {
                    minDate: date.start,
                    maxDate: date.end,
                } : {}),
                page,
                perPage,
            }

            this.isLoading = true
            apiRequest(Api().imports.list(params))
                .then(({ meta, data }) => {
                    this.pagination = meta
                    this.imports = data
                })
                .catch(rethrowError(notifyError()))
                .finally(() => setTimeout(() => (this.isLoading = false), 1000))
        },
        cancelImport () {
            const { id } = this.selected
            this.isFetching = true
            apiRequest(Api().imports.cancel(id))
                .then(({ data }) => {
                    notifySuccess(this.$t('imports.modals.cancelSuccess'))()
                    this.fetchImports()
                })
                .catch(rethrowError(notifyError()))
                .finally(() => {
                    this.isFetching = false
                    this.handleCleanModal()
                })
        },
        updateImport (params) {
            const { id } = this.selected
            this.isFetching = true
            apiRequest(Api().imports.update(id, params))
                .then(({ data }) => {
                    notifySuccess(this.$t('imports.modals.archiveSuccess'))()
                    this.fetchImports()
                })
                .catch(rethrowError(notifyError()))
                .finally(() => {
                    this.isFetching = false
                    this.handleCleanModal()
                })
        },
        archiveImport () {
            this.updateImport({ archived: true })
        },
        unarchiveImport () {
            this.updateImport({ archived: false })
        },
        handleResetFilters () {
            this.currentFilters = { ...CURRENT_FILTERS }
            this.fetchImports()
        },
        handleCleanModal () {
            this.selected = undefined
            this.modal = undefined
        },
        handleNewImport () {
            this.modal = this.modals.CREATE
        },
        handleEvolution () {
            this.modal = this.modals.EVOLUTION
        },
        handleArchive (item) {
            this.selected = item
            this.modal = this.modals.ARCHIVE
        },
        handleUnarchive (item) {
            this.selected = item
            this.modal = this.modals.UNARCHIVE
        },
        handleCancel (item) {
            this.selected = item
            this.modal = this.modals.CANCEL
        },
        handleView ({ id, status }) {
            if ([STATUS.completed].includes(status)) {
                this.$router.push({ name: 'AdminImportCreatePreview', params: { id } })
            } else if ([STATUS.pendingReview, STATUS.cancelled, STATUS.failed].includes(status)) {
                this.$router.push({ name: 'AdminImportsCreateValidation', params: { id } })
            }
        },
        handleCreateImport ({ id, status }) {
            const name = STATUS.completed === status ? 'AdminImportCreatePreview' : 'AdminImportsGenerate'
            this.handleCleanModal()
            setTimeout(() => this.$router.push({ name, params: { id } }), 600)
        },
        /**
         * Descarga el fichero en formato xls.
         * Si no existe el fichero, muestra un error por pantalla.
         * @param {number} item Identificador del fichero.
         * @param {string} name Nombre de la importación que se utiliza para el nombre del fichero.
         */
        handleDownload ({ fileUploadId, name }) {
            apiRequest(Api().imports.importDownload(fileUploadId))
                .then((res) => { fileDownload(res, `${name}.csv`) })
                .catch(rethrowError(notifyError()))
        },
    },
}
</script>

<style lang="scss" scoped>
.Container {
    position: relative;
    min-height: calc(100vh - 255px);

    .Wrapper {
        width: 100%;
    }
}

.Filters {
    padding: 15px;
}

.List {
    .ImportItem + .ImportItem {
        border-top: 1px solid $gray-05;
    }
}

.Tabs {
    height: 35px;
}
</style>
